// ProfilMetierTenant — onglet « Profil métier » d'AdministrationTenant (T-330 / M-4). Super admin plateforme seulement.
//
// 🔑 N'INVENTE RIEN : il lit et écrit les clés déclarées dans `TenantConfig` par
//   GET  /api/platform/tenants/{slug}/profil-metier         (valeurs + listes fermées)
//   POST /api/platform/tenants/{slug}/profil-metier/ecarts  (écarts avec le préréglage du métier, n'écrit RIEN)
//   PUT  /api/platform/tenants/{slug}/profil-metier         (écrit, le serveur journalise)
// ⛔ Rien n'est écrit sans que les changements soient affichés (avant → après) puis confirmés.
// Kit commun (oh-charte) et jetons var(--oh-*) ; aucune couleur en dur.
import { useState, useEffect, useCallback, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { OhField, OhSelect, OhStatusNote } from "../../../components/ui/oh-charte";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}`, "Content-Type": "application/json" });

const REGIMES = { anah: "ANAH (MaPrimeAdapt')", mpr: "MaPrimeRénov'", cee: "Certificats d'économie d'énergie" };
const TEXTE = { true: "Oui", false: "Non" };

const afficher = (v) => (v === null || v === undefined || v === "" ? "non renseigné"
  : Array.isArray(v) ? (v.length ? v.map((x) => REGIMES[x] || x).join(", ") : "aucune")
    : typeof v === "boolean" ? TEXTE[v] : String(v));
const meme = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const bouton = (principal) => ({
  background: principal ? "var(--oh-accent-solid)" : "transparent",
  color: principal ? "var(--oh-accent-contrast)" : "var(--oh-accent-text)",
  border: principal ? "none" : "0.5px solid var(--oh-accent)",
  borderRadius: 8, padding: "6px 12px", fontSize: 14,
});

export default function ProfilMetierTenant({ slug, onModifie }) {
  const [etat, setEtat] = useState(null);       // réponse du GET
  const [brouillon, setBrouillon] = useState({});
  const [ecarts, setEcarts] = useState(null);   // écarts du préréglage, affichés avant d'écrire
  const [recap, setRecap] = useState(false);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true); setErreur(null);
    try {
      const r = await fetch(`${API}/api/platform/tenants/${slug}/profil-metier`, { headers: auth(), credentials: "include" });
      if (!r.ok) throw new Error(r.status === 403 ? "Réservé à l'administrateur de la plateforme." : "Impossible de lire le profil métier.");
      const j = await r.json();
      setEtat(j); setBrouillon(j.valeurs);
    } catch (e) { setErreur(e.message); } finally { setChargement(false); }
  }, [slug]);
  useEffect(() => { charger(); }, [charger]);

  const changes = useMemo(() => (etat ? etat.champs.filter((c) => !meme(brouillon[c.cle], etat.valeurs[c.cle]))
    .map((c) => ({ cle: c.cle, libelle: c.libelle, actuel: etat.valeurs[c.cle], propose: brouillon[c.cle] })) : []), [etat, brouillon]);
  const libelle = (cle) => etat?.champs.find((c) => c.cle === cle)?.libelle || cle;

  const ecrire = async (liste) => {
    setEnCours(true); setErreur(null);
    try {
      const r = await fetch(`${API}/api/platform/tenants/${slug}/profil-metier`, {
        method: "PUT", headers: auth(), credentials: "include",
        body: JSON.stringify({ cles: Object.fromEntries(liste.map((c) => [c.cle, c.propose])), motif: "Écran Gouvernance · Profil métier" }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Écriture refusée : rien n'a été modifié.");
      toast.success("Profil métier enregistré");
      setRecap(false); setEcarts(null);
      await charger();
      onModifie?.();
    } catch (e) { setErreur(e.message); } finally { setEnCours(false); }
  };

  const voirEcarts = async () => {
    setEnCours(true); setErreur(null);
    try {
      const r = await fetch(`${API}/api/platform/tenants/${slug}/profil-metier/ecarts`, {
        method: "POST", headers: auth(), credentials: "include", body: JSON.stringify({ business_model: brouillon.business_model || null }),
      });
      if (!r.ok) throw new Error("Impossible de calculer les écarts du préréglage.");
      setEcarts((await r.json()).ecarts);
    } catch (e) { setErreur(e.message); } finally { setEnCours(false); }
  };

  if (chargement) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--oh-text-muted)" }} /></div>;
  if (!etat) return <OhStatusNote variant="error" data-testid="profil-erreur">{erreur || "Profil indisponible."}</OhStatusNote>;

  const champ = ({ cle, libelle: lb, type }) => {
    const choix = etat.choix[cle] || [];
    const id = `profil-${cle}`;
    if (type === "liste") {
      const val = brouillon[cle] || [];
      return (
        <OhField key={cle} label={lb} className="mb-3" hint="Détermine les aides proposées dans l'onglet Aides.">
          <div className="flex gap-3 flex-wrap">
            {choix.map((x) => (
              <label key={x} className="text-sm flex items-center gap-1" style={{ color: "var(--oh-text)" }}>
                <input type="checkbox" checked={val.includes(x)} data-testid={`${id}-${x}`}
                       onChange={() => setBrouillon((p) => ({ ...p, [cle]: val.includes(x) ? val.filter((y) => y !== x) : [...val, x] }))} />
                {REGIMES[x] || x}
              </label>
            ))}
          </div>
        </OhField>
      );
    }
    const options = type === "oui_non" ? [["true", "Oui"], ["false", "Non"]] : choix.map((x) => [x, x]);
    const courant = brouillon[cle] === null || brouillon[cle] === undefined ? "" : String(brouillon[cle]);
    return (
      <OhField key={cle} label={lb} htmlFor={id} className="mb-3">
        <OhSelect id={id} value={courant} data-testid={id}
                  onChange={(e) => setBrouillon((p) => ({ ...p, [cle]: e.target.value === "" ? null : (type === "oui_non" ? e.target.value === "true" : e.target.value) }))}>
          <option value="">Non renseigné</option>
          {options.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
        </OhSelect>
      </OhField>
    );
  };

  const tableau = (lignes, testid) => (
    <div data-testid={testid} className="rounded-lg p-3 mb-3 text-sm"
         style={{ background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", color: "var(--oh-text)" }}>
      {lignes.map((l) => (
        <div key={l.cle} className="py-1" style={{ borderBottom: "0.5px solid var(--oh-border)" }}>
          <strong>{libelle(l.cle)}</strong>&nbsp;: {afficher(l.actuel)} <span style={{ color: "var(--oh-text-muted)" }}>→</span> {afficher(l.propose)}
        </div>
      ))}
    </div>
  );

  return (
    <div data-testid="profil-metier">
      <p className="text-xs mb-3" style={{ color: "var(--oh-text-secondary)" }}>
        Ces réglages décident des écrans et des aides de « {etat.nom} ». Rien n'est écrit tant que vous n'avez pas confirmé,
        et chaque changement est noté dans le journal.
      </p>
      {erreur && <OhStatusNote variant="error" className="mb-3" data-testid="profil-erreur">{erreur}</OhStatusNote>}
      {etat.champs.map(champ)}

      {ecarts && (
        <div className="mb-3" data-testid="profil-ecarts">
          {ecarts.length === 0
            ? <OhStatusNote variant="success">Ce tenant est déjà conforme au préréglage de son métier.</OhStatusNote>
            : <>
                <p className="text-sm mb-2" style={{ color: "var(--oh-text)" }}>Le préréglage du métier changerait&nbsp;:</p>
                {tableau(ecarts.map((e) => ({ cle: e.cle, actuel: e.actuel, propose: e.propose })), "profil-ecarts-liste")}
                <div className="flex gap-2">
                  <button type="button" style={bouton(true)} disabled={enCours} data-testid="profil-ecarts-appliquer"
                          onClick={() => ecrire(ecarts.map((e) => ({ cle: e.cle, propose: e.propose })))}>Appliquer ces changements</button>
                  <button type="button" style={bouton(false)} onClick={() => setEcarts(null)} data-testid="profil-ecarts-annuler">Laisser tel quel</button>
                </div>
              </>}
        </div>
      )}

      {recap && (
        <div className="mb-3" data-testid="profil-recap">
          <p className="text-sm mb-2" style={{ color: "var(--oh-text)" }}>Vous allez changer&nbsp;:</p>
          {tableau(changes, "profil-recap-liste")}
          <div className="flex gap-2">
            <button type="button" style={bouton(true)} disabled={enCours} data-testid="profil-confirmer" onClick={() => ecrire(changes)}>Confirmer et enregistrer</button>
            <button type="button" style={bouton(false)} onClick={() => setRecap(false)} data-testid="profil-recap-annuler">Revenir</button>
          </div>
        </div>
      )}

      {!recap && !ecarts && (
        <div className="flex gap-2 flex-wrap">
          <button type="button" style={{ ...bouton(true), opacity: changes.length ? 1 : 0.5 }} disabled={!changes.length || enCours}
                  data-testid="profil-enregistrer" onClick={() => setRecap(true)}>Enregistrer les changements</button>
          <button type="button" style={bouton(false)} disabled={enCours || !brouillon.business_model} data-testid="profil-preset" onClick={voirEcarts}>
            Appliquer le préréglage du métier
          </button>
        </div>
      )}
    </div>
  );
}
